/**
 * RFI Send Dialog (Feb 2026, Phase A)
 * ====================================
 *
 * Per-RFI recipient picker + dispatch. Talks to POST /api/rfis/{id}/send
 * which mirrors the RFP send rails: per-recipient dispatch through Resend,
 * `pending → sent` flip ONLY on actual delivery, partial-failure response
 * shape, contact-timeline activity capture, and the test-fixture override
 * (every email rewritten to delivered@resend.dev when the RFI belongs to
 * the standing super-admin sandbox).
 *
 * data-testids:
 *   rfi-send-dialog
 *   rfi-send-recipient-row-{contact_id}
 *   rfi-send-recipient-checkbox-{contact_id}
 *   rfi-send-add-contact-search
 *   rfi-send-add-from-contacts-btn
 *   rfi-send-confirm-btn
 *   rfi-send-cancel-btn
 *   rfi-send-result-block
 */

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send, Loader2, X, Plus, Mail, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RFISendDialog({ open, onOpenChange, rfi, onSent }) {
  const [recipients, setRecipients] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Seed dialog state from the current RFI whenever it opens.
  // Newly-pending recipients are auto-selected so the create→send flow can
  // hit Send immediately without manually ticking each row.
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setRecipients((Array.isArray(rfi?.recipients) ? rfi.recipients : []).map((r) => ({
      ...r,
      _selected: r.status === 'pending',
    })));
    setPickerOpen(false);
    setContactSearch('');
  }, [open, rfi]);

  // Lazy-fetch contacts for the picker (the company's contact directory).
  //
  // Hardening (Feb 2026 — production bug fix): hit `/contacts/list` (the
  // proven endpoint the main Contacts directory uses) and handle both
  // response shapes (bare list OR `{contacts: [...]}`). Surface errors
  // loudly so empty-picker bugs don't slip through silently in prod.
  useEffect(() => {
    if (!pickerOpen) return;
    axios.get(`${API}/contacts/list`)
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
        setContactOptions(data);
        if (data.length === 0) {
          console.warn('[RFISendDialog] /contacts/list returned 0 contacts for this tenant');
        }
      })
      .catch((err) => {
        console.error('[RFISendDialog] Failed to load contacts:', err);
        setContactOptions([]);
      });
  }, [pickerOpen]);

  const recipientIds = useMemo(
    () => new Set(recipients.map((r) => r.contact_id || r.contractor_id)),
    [recipients],
  );

  const filteredContacts = useMemo(() => {
    if (!contactSearch) return contactOptions.slice(0, 50);
    const q = contactSearch.toLowerCase();
    return contactOptions
      .filter((c) => {
        const name = `${c.first_name || ''} ${c.last_name || ''} ${c.company || ''}`.toLowerCase();
        return name.includes(q) || (c.email || '').toLowerCase().includes(q);
      })
      .slice(0, 50);
  }, [contactOptions, contactSearch]);

  const toggleRecipient = (id) => {
    setRecipients((rs) => rs.map((r) => {
      const rid = r.contact_id || r.contractor_id;
      if (rid !== id) return r;
      // Toggle the `_selected` flag — actual `status` from the RFI doc
      // stays unchanged. Only `_selected` recipients are sent.
      return { ...r, _selected: !r._selected };
    }));
  };

  const addRecipientFromContact = (contact) => {
    if (!contact?.email) {
      toast.error('Contact has no email — add one in Contacts first.');
      return;
    }
    const id = contact.id;
    if (recipientIds.has(id)) {
      toast.info('Already in recipient list.');
      return;
    }
    const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.company || contact.email;
    setRecipients((rs) => [
      ...rs,
      {
        contact_id: id,
        contractor_id: id,
        name,
        email: contact.email,
        status: 'pending',
        _selected: true,
        _newly_added: true,
      },
    ]);
    setPickerOpen(false);
    setContactSearch('');
  };

  const removeRecipient = (id) => {
    setRecipients((rs) => rs.filter((r) => (r.contact_id || r.contractor_id) !== id));
  };

  const send = async () => {
    if (!rfi?.id) return;
    const selected = recipients.filter((r) => r._selected && r.email);
    if (selected.length === 0) {
      toast.error('Pick at least one recipient.');
      return;
    }
    setSending(true);
    try {
      // Single round-trip: persist the updated recipients[] AND fire the send
      // in one call. The route accepts both `recipients` (full array to persist)
      // and `recipient_ids` (the subset to dispatch right now) in the body.
      const persisted = recipients.map((r) => {
        // Drop local-only flags before persisting.
        const clean = { ...r };
        delete clean._selected;
        delete clean._newly_added;
        return clean;
      });
      const recipient_ids = selected.map((r) => r.contractor_id || r.contact_id);
      const res = await axios.post(`${API}/rfis/${rfi.id}/send`, {
        recipients: persisted,
        recipient_ids,
      });
      setResult(res.data);
      if (res.data.sent > 0) {
        toast.success(`Sent ${res.data.sent} of ${res.data.attempted} recipient(s).`);
      } else if (res.data.failed?.length) {
        toast.error(`All ${res.data.attempted} sends failed — see details.`);
      } else if (res.data.skipped_no_email?.length) {
        toast.error('Some recipients had no email and were skipped.');
      } else {
        toast.info('No recipients dispatched.');
      }
      if (onSent) onSent(res.data);
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rfi-send-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send {rfi?.rfi_number || 'RFI'}
          </DialogTitle>
          <DialogDescription>
            Picks ride the same dispatch rails as RFP sends — emails go through Resend,
            status flips only on successful delivery, and each send lands on the
            recipient&apos;s contact timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Recipient list */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Recipients</div>
            {recipients.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">
                No recipients yet — add one from your contacts below.
              </div>
            ) : (
              recipients.map((r) => {
                const id = r.contact_id || r.contractor_id;
                const isSent = r.status === 'sent';
                // RS-8 — a vendor who answered at their own address.
                const isAnswered = r.status === 'answered';
                const isResponded = r.status === 'responded' || isAnswered;
                const when = (value) => {
                  if (!value) return '';
                  const d = new Date(value);
                  return Number.isNaN(d.getTime()) ? '' : ` ${d.toLocaleDateString()}`;
                };
                return (
                  <div
                    key={id}
                    data-testid={`rfi-send-recipient-row-${id}`}
                    className="flex items-center gap-3 p-2 rounded border bg-muted/30"
                  >
                    <Checkbox
                      data-testid={`rfi-send-recipient-checkbox-${id}`}
                      checked={!!r._selected}
                      onCheckedChange={() => toggleRecipient(id)}
                      disabled={isSent && !r._newly_added}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.name || r.email}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        {r.email || <span className="italic text-red-500">no email</span>}
                      </div>
                    </div>
                    {isResponded && (
                      <Badge variant="outline" className="text-green-600 border-green-600"
                        data-testid={`rfi-send-recipient-state-${id}`}>
                        {isAnswered ? `Answered${when(r.answered_at)}` : 'Responded'}
                      </Badge>
                    )}
                    {isSent && !isResponded && (
                      <Badge variant="outline" className="text-blue-600 border-blue-600"
                        data-testid={`rfi-send-recipient-state-${id}`}>
                        {`Sent${when(r.sent_at)}`}
                      </Badge>
                    )}
                    {!isSent && !isResponded && (
                      <Badge variant="outline" className="text-muted-foreground"
                        data-testid={`rfi-send-recipient-state-${id}`}>
                        Pending
                      </Badge>
                    )}
                    {!isSent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRecipient(id)}
                        aria-label="Remove"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Picker */}
          {!pickerOpen ? (
            <Button
              data-testid="rfi-send-add-from-contacts-btn"
              variant="outline"
              size="sm"
              onClick={() => setPickerOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add from contacts
            </Button>
          ) : (
            <div className="border rounded p-2 space-y-2">
              <Input
                data-testid="rfi-send-add-contact-search"
                placeholder="Search by name, company, or email…"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                autoFocus
              />
              <div className="max-h-48 overflow-y-auto">
                {filteredContacts.length === 0 ? (
                  <div className="text-xs text-muted-foreground p-2">No matching contacts.</div>
                ) : (
                  filteredContacts.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full text-left p-2 rounded hover:bg-muted text-sm flex items-center justify-between gap-2"
                      onClick={() => addRecipientFromContact(c)}
                      disabled={recipientIds.has(c.id)}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company || c.email || c.id}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.email || 'no email'}{c.company ? ` · ${c.company}` : ''}</div>
                      </div>
                      {recipientIds.has(c.id) && (
                        <span className="text-xs text-muted-foreground italic">added</span>
                      )}
                    </button>
                  ))
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPickerOpen(false); setContactSearch(''); }}
              >
                Done
              </Button>
            </div>
          )}

          {/* Result panel (visible after send) */}
          {result && (
            <div data-testid="rfi-send-result-block" className="rounded border p-3 space-y-1 bg-muted/40 text-sm">
              <div className="flex items-center gap-2 font-medium">
                {result.sent > 0
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                Dispatch complete — {result.sent} of {result.attempted} sent.
              </div>
              {result.failed?.length > 0 && (
                <div>
                  <div className="font-medium text-red-700">Failed:</div>
                  <ul className="text-xs pl-4 list-disc">
                    {result.failed.map((f, i) => (
                      <li key={i}>{f.email}: {f.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.skipped_no_email?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Skipped (no email): {result.skipped_no_email.length}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="rfi-send-cancel-btn" variant="outline" onClick={close} disabled={sending}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          <Button
            data-testid="rfi-send-confirm-btn"
            onClick={send}
            disabled={sending || result?.sent > 0}
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
